import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import StateBadge from "@/components/StateBadge";
import { useTenantPath } from "@/lib/tenantPath";
import { useRole } from "@/lib/roles";
import { formatDatePT } from "@/lib/format";
import { Plus, CalendarDays } from "lucide-react";

export default function Events() {
  const { t } = useTranslation("events");
  const { t: tc } = useTranslation("common");
  const navigate = useNavigate();
  const tPath = useTenantPath();
  const { canEdit } = useRole();
  const [events, setEvents] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [sortDir, setSortDir] = useState("asc"); // asc = mais antigos primeiro, desc = mais recentes primeiro
  const [when, setWhen] = useState("upcoming"); // upcoming | past | all
  const [clientFilter, setClientFilter] = useState(""); // client_id
  const [datePeriod, setDatePeriod] = useState(""); // "" | "7" | "15" | "30" | "custom"
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const todayISO = new Date().toISOString().slice(0, 10);
  const addDaysISO = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
  const matchesDatePeriod = (d) => {
    if (!datePeriod) return true;
    if (datePeriod === "custom") {
      if (customFrom && d < customFrom) return false;
      if (customTo && d > customTo) return false;
      return true;
    }
    return d >= todayISO && d <= addDaysISO(Number(datePeriod));
  };
  const filteredByWhen = events.filter((e) => {
    const d = String(e.date || "").slice(0, 10);
    if (clientFilter && e.client_id !== clientFilter) return false;
    if (!matchesDatePeriod(d)) return false;
    if (when === "upcoming") return d >= todayISO;
    if (when === "past") return d < todayISO;
    return true;
  });
  const clientName = (id) => clients.find((c) => c.id === id)?.name || "";
  const sortedEvents = [...filteredByWhen].sort((a, b) => {
    const cmp = String(a.date || "").localeCompare(String(b.date || ""));
    return sortDir === "asc" ? cmp : -cmp;
  });

  const STATUS_LABELS = {
    "": t("statusFilter.all"),
    rascunho: t("statusFilter.rascunho"),
    a_contactar: t("statusFilter.a_contactar"),
    parcialmente_preenchido: t("statusFilter.parcialmente_preenchido"),
    confirmado: t("statusFilter.confirmado"),
    concluido: t("statusFilter.concluido"),
    cancelado: t("statusFilter.cancelado"),
  };

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status_filter = statusFilter;
    api.get("/events", { params }).then((r) => setEvents(r.data)).finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="events-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-zinc-950">{t("title")}</h1>
          <p className="text-sm text-zinc-500 mt-1">{t("subtitle", { count: events.length })}</p>
        </div>
        <Link to={tPath("/eventos/novo")} data-testid="new-event-button" className={`inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm font-semibold ${canEdit ? "" : "hidden"}`}>
          <Plus className="w-4 h-4" /> {t("newEvent")}
        </Link>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {[["upcoming", "Próximos"], ["past", "Passados"], ["all", "Todos"]].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setWhen(k)}
            data-testid={`events-when-${k}`}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${when === k ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}
          >
            {label} <span className={when === k ? "text-zinc-300" : "text-zinc-400"}>({k === "upcoming" ? events.filter((e) => String(e.date || "").slice(0, 10) >= todayISO).length : k === "past" ? events.filter((e) => String(e.date || "").slice(0, 10) < todayISO).length : events.length})</span>
          </button>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl p-4 flex flex-wrap gap-3 items-center">
        <select data-testid="filter-event-client" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm bg-white">
          <option value="">Todos os clientes</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select data-testid="filter-event-period" value={datePeriod} onChange={(e) => setDatePeriod(e.target.value)} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm bg-white">
          <option value="">Qualquer data</option>
          <option value="7">Próximos 7 dias</option>
          <option value="15">Próximos 15 dias</option>
          <option value="30">Próximos 30 dias</option>
          <option value="custom">Data personalizada…</option>
        </select>
        {datePeriod === "custom" && (
          <div className="flex items-center gap-2">
            <input type="date" data-testid="filter-event-date-from" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm bg-white" />
            <span className="text-zinc-400 text-sm">até</span>
            <input type="date" data-testid="filter-event-date-to" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm bg-white" />
          </div>
        )}
        <select data-testid="filter-event-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm bg-white">
          {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select data-testid="sort-event-date" value={sortDir} onChange={(e) => setSortDir(e.target.value)} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm bg-white">
          <option value="asc">Data ↑ (mais antigos primeiro)</option>
          <option value="desc">Data ↓ (mais recentes primeiro)</option>
        </select>
        {(clientFilter || datePeriod) && (
          <button data-testid="clear-event-filters" onClick={() => { setClientFilter(""); setDatePeriod(""); setCustomFrom(""); setCustomTo(""); }} className="text-sm text-zinc-500 hover:text-zinc-900 hover:underline ml-auto">
            Limpar filtros
          </button>
        )}
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        {loading ? <div className="p-10 text-center text-sm text-zinc-500">{tc("states.loading")}</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50/50 text-xs font-semibold text-zinc-500 uppercase tracking-wider border-b border-zinc-200">
                <tr>
                  <th className="text-left py-3 px-4">{t("table.event")}</th>
                  <th className="text-left py-3 px-4 hidden md:table-cell">{t("table.date")}</th>
                  <th className="text-left py-3 px-4 hidden lg:table-cell">{t("table.location")}</th>
                  <th className="text-right py-3 px-4">{t("table.slots")}</th>
                  <th className="text-right py-3 px-4">{t("table.status")}</th>
                </tr>
              </thead>
              <tbody>
                {sortedEvents.map((e) => {
                  const slots = e.slots_effective ?? e.slots_needed;
                  const confirmed = e.confirmed_effective ?? e.confirmed_ids.length;
                  const available = e.accepted_effective ?? (e.shifts_enabled ? 0 : (e.available_ids || []).filter((p) => !(e.confirmed_ids || []).includes(p)).length);
                  const ratio = slots ? confirmed / slots : 0;
                  return (
                    <tr key={e.id} data-testid={`event-row-${e.id}`} onClick={() => navigate(tPath(`/eventos/${e.id}`))} className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer transition-colors">
                      <td className="py-3 px-4">
                        <div className="font-medium text-zinc-900">{e.name}{e.code && <span className="ml-2 text-xs font-mono text-zinc-400">{e.code}</span>}</div>
                        {e.client_id && clientName(e.client_id) && <div className="text-xs text-zinc-500">{clientName(e.client_id)}</div>}
                        <div className="text-xs text-zinc-500 md:hidden">{formatDatePT(e.date)} · {e.location}</div>
                      </td>
                      <td className="py-3 px-4 text-zinc-700 hidden md:table-cell">
                        <div className="inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-zinc-400" />{formatDatePT(e.date)}</div>
                      </td>
                      <td className="py-3 px-4 text-zinc-700 hidden lg:table-cell">{e.location}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="inline-flex items-center gap-2">
                          <div className="w-16 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                            <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, ratio * 100)}%` }} />
                          </div>
                          <span className="text-xs text-zinc-700">
                            <span className="font-semibold text-zinc-950">{confirmed}/{slots}</span>
                            {available > 0 && <span className="text-emerald-700"> +{available}</span>}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right"><StateBadge status={e.status} /></td>
                    </tr>
                  );
                })}
                {sortedEvents.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-sm text-zinc-500">{when === "upcoming" ? "Sem eventos próximos." : when === "past" ? "Sem eventos passados." : t("empty")}</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
